import React, { useState } from "react";
import { Box, TextField, FormControl, FormHelperText, Stack, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSubmit } from "../hooks/useSubmit";
import { useSelector, useDispatch } from "react-redux";
import { setHearAbt as updateHearAbt } from "../redux/userSlice";
import { continueButtonSx, inputLabelSx, moveButtonSx, textAreaSx } from "../lib/uiformatting";
import Question from "../components/Question";
import MoveButton from "../components/MoveButton";
import ContinueButton from "../components/ContinueButton";
import setPageAndDeferNavigate from "../lib/setPageAndDeferNavigate";

export default function HearAbt() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const theme = useTheme();
    const [canSubmit, handleSubmit] = useSubmit();
    const [processing, setProcessing] = useState(false);

    const options = {
        "Google": "Google",
        "Facebook/Instagram": "Facebook/Instagram",
        "ZocDoc or another medical website": "ZocDoc or another medical website",
        "A friend or family": "A friend or family",
        "My doctor": "My doctor",
        "I'm already a patient": "I'm already a patient",
    };

    const user = useSelector((state) => state.user);
    const [method, setMethod] = useState(
        user.zocDoc ? "ZocDoc or another medical website" : user.hearAbt
    );

    const isValidSelection =
        Object.keys(options).includes(method) ||
        (method.trim() !== "" && !Object.values(options).includes(method));
    const hasError = canSubmit === "error";
    const canProceed = canSubmit === "";

    const handleSubmitAndRedirect = async () => {
        dispatch(updateHearAbt(method));
        setProcessing(true);
        const res = await handleSubmit();
        if (res) {
            if (user.portal) {
                navigate('/case-list');
            } else if (user.treatmentContinuation?.interestedInAccutane === "Y" && (user.address?.state === "CT" || user.address?.state === "MA")) {
                navigate('/isotretinoin-thank-you');
            } else {
                if (user.payMethod === "insurance") {
                    navigate('/thank-you');
                } else {
                    navigate('/self-thank-you');
                }
            }
        } else {
            setProcessing(false);
        }
    };

    const handleBack = () => {
        setPageAndDeferNavigate(dispatch, '/address', navigate);
    };

    return (
        <Question
            onBack={handleBack}
            title="How did you hear about us?"
            input={
                <Box sx={{ display: "flex", flexDirection: "column", gap: "1.5rem", width: "100%", justifyContent: "center" }}>
                    <FormControl fullWidth>
                        <Stack spacing={2} direction="column">
                            {Object.keys(options).map((key) => (
                                <MoveButton
                                    sx={{ ...moveButtonSx }}
                                    key={key}
                                    onClick={() => {
                                        setMethod(key);
                                        dispatch(updateHearAbt(key));
                                    }}
                                    title={options[key]}
                                    selected={method === key}
                                />
                            ))}
                        </Stack>
                        <TextField
                            label="Other"
                            variant="outlined"
                            color="secondary"
                            multiline
                            rows={1}
                            sx={{
                                ...textAreaSx,
                                mt: 3,
                                borderColor: "grayTones.light",
                                "& .MuiInputLabel-root": {
                                    backgroundColor: theme.palette.background.paper,
                                    padding: "0 16px",
                                    transition: "all 0.2s ease-in-out",
                                },
                            }}
                            autoComplete="off"
                            value={!Object.keys(options).includes(method) ? method : ""}
                            onChange={(e) => {
                                setMethod(e.target.value);
                                dispatch(updateHearAbt(e.target.value));
                            }}
                            InputLabelProps={{
                                sx: {
                                    ...inputLabelSx,
                                    "& .MuiInputLabel-shrink": {
                                        backgroundColor: theme.palette.background.paper,
                                    },
                                },
                            }}
                        />
                    </FormControl>
                </Box>
            }
            footer={
                <Box
                    sx={{
                        ...continueButtonSx,
                        flexDirection: "column",
                        alignItems: "center",
                        position: "sticky",
                        bottom: 0,
                    }}
                >
                    <ContinueButton
                        onClick={handleSubmitAndRedirect}
                        disabled={processing || !isValidSelection || hasError || !canProceed}
                        selected={!processing && isValidSelection && canProceed && !hasError}
                        title={processing ? "Processing..." : "Submit"}
                    />
                    {canSubmit && (
                        <FormHelperText error sx={{ fontSize: "0.85rem", mt: 1 }}>
                            {canSubmit === "pay"
                                ? "Please make sure your payment details have been entered."
                                : canSubmit === "fields"
                                    ? "Some required fields have not been filled out. Please go back and double-check your information."
                                    : canSubmit === "error"
                                        ? "There was an error submitting your information. Please try again, and contact support (call or text 781-650-4492) if the issue persists."
                                        : ""}
                        </FormHelperText>
                    )}
                </Box>
            }
        />
    );
}
